import { useId } from "react";
import { useTranslation } from "react-i18next";
import { InfoCircleOutlined } from "@ant-design/icons";

import BaseSkeleton from "@/components_v2/Skeleton/BaseSkeleton";
import BaseSkeletonGroup from "@/components_v2/Skeleton/BaseSkeletonGroup";
import { SKELETON_VARIANTS } from "@/components_v2/Skeleton/constants";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import {
  ANALYTICS_SKELETON_LINES,
  CARD_SIZES,
} from "@/features/certifications/analytics/constants";
import { AnalyticsCardProps } from "@/features/certifications/analytics/types";

import "../styles.css";

const AnalyticsCard = ({
  title,
  info,
  aside,
  isLoading,
  isError,
  size = CARD_SIZES.AUTO,
  children,
}: AnalyticsCardProps) => {
  const { t } = useTranslation();
  const titleId = useId();

  let content = children;
  if (isLoading) {
    content = (
      <BaseSkeletonGroup label={t("labels.loading")}>
        <BaseSkeleton
          variant={SKELETON_VARIANTS.TEXT}
          lines={ANALYTICS_SKELETON_LINES}
        />
      </BaseSkeletonGroup>
    );
  } else if (isError) {
    content = (
      <p className="analytics-muted body-s-regular" role="alert">
        {t("certification.admin.analytics.error")}
      </p>
    );
  }

  return (
    <section
      aria-labelledby={titleId}
      className={`analytics-card analytics-card-${size} flex flex-col gap-400`}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-200">
        <span className="flex items-center gap-100">
          <h3 id={titleId} className="analytics-title body-m-medium">
            {title}
          </h3>

          {info && (
            <BaseTooltip title={info}>
              <InfoCircleOutlined
                className="analytics-info"
                aria-label={info}
                role="img"
                tabIndex={0}
              />
            </BaseTooltip>
          )}
        </span>

        {!isLoading && !isError && aside}
      </header>

      {content}
    </section>
  );
};

export default AnalyticsCard;
